import { getComponentsByType } from '@/utils/utils';

export function buildCanvasContext(store, hooks = {}) {
  const state = store.state;
  const back = (state.componentData || []).find((c) => c.component === 'Back') || null;
  const components = (state.componentData || [])
    .filter((c) => c.component !== 'Back')
    .map((c) => ({
      id: String(c.id),
      component: c.component,
      type: c.type,
      label: c.label,
      alias: c.style && c.style.alias,
      text: typeof c.propValue === 'string' ? c.propValue : undefined,
      left: c.style && c.style.left,
      top: c.style && c.style.top,
      width: c.style && c.style.width,
      height: c.style && c.style.height,
    }));

  const limits = hooks.getRouteLimits ? hooks.getRouteLimits() : {};
  const buttonCount = hooks.getButtonCountEstimate
    ? hooks.getButtonCountEstimate()
    : getComponentsByType(state.componentData, 'v-button').length;

  const areaList = ((state.areaData && state.areaData.components) || [])
    .filter((c) => c && c.id && !idsEqual(c.id, 'Back'))
    .map((c) => ({
      id: String(c.id),
      component: c.component,
      alias: c.style && c.style.alias,
    }));

  return {
    currentPageId: state.selectedCurrentPage,
    pageOptions: state.pageOptions,
    projectStyle: state.projectStyleData,
    canvasStyle: state.canvasStyleData,
    back: back
      ? {
          pageName: back.style.pageName,
          backgroundColor: back.style.backgroundColor,
          grid: back.style.grid,
          pageWidth: back.style.pageWidth,
          pageHeight: back.style.pageHeight,
          isShowMenu: back.style.isShowMenu,
          opacity: back.style.opacity,
        }
      : null,
    components,
    componentCount: components.length,
    buttonCount,
    limits,
    selection: state.curComponent && state.curComponent.id !== 'Back'
      ? {
          id: String(state.curComponent.id),
          component: state.curComponent.component,
          alias: state.curComponent.style && state.curComponent.style.alias,
        }
      : null,
    areaSelection: areaList,
  };
}

export function idsEqual(a, b) {
  if (a == null || b == null || a === "" || b === "") {
    return false;
  }
  if (a === b) {
    return true;
  }
  if (String(a) === String(b)) {
    return true;
  }
  const n1 = Number(a);
  const n2 = Number(b);
  return Number.isFinite(n1) && Number.isFinite(n2) && n1 === n2;
}

export function findComponentByRef(store, ref) {
  const list = store.state.componentData || [];
  if (ref == null || ref === "") {
    return null;
  }
  if (typeof ref === "object" && ref.id != null) {
    return findComponentByRef(store, ref.id);
  }
  const s = String(ref).trim();
  let found = null;
  walkComponents(list, (c) => {
    if (found) return;
    if (idsEqual(c.id, s)) {
      found = c;
      return;
    }
    if (c.style && c.style.alias != null && String(c.style.alias) === s) {
      found = c;
      return;
    }
    if (c.propValue != null && typeof c.propValue !== "object" && String(c.propValue) === s) {
      found = c;
    }
  });
  return found;
}

function walkComponents(list, visit) {
  (list || []).forEach((c) => {
    if (!c) return;
    visit(c);
    if (c.component === "Group" && Array.isArray(c.propValue)) {
      walkComponents(c.propValue, visit);
    }
  });
}

export function collectLiveTargets(store, extraIds) {
  const extras = (extraIds || [])
    .map((id) => findComponentByRef(store, id))
    .filter((c) => c && !idsEqual(c.id, "Back"));
  if (extras.length) {
    return extras;
  }
  const fromArea = ((store.state.areaData && store.state.areaData.components) || [])
    .map((c) => findComponentByRef(store, c && (c.id != null ? c.id : c)))
    .filter((c) => c && !idsEqual(c.id, "Back"));
  if (fromArea.length) {
    return fromArea;
  }
  const list = store.state.componentData || [];
  const cur = store.state.curComponent;
  if (cur && !idsEqual(cur.id, "Back")) {
    const live = findComponentByRef(store, cur.id) || list.find((c) => idsEqual(c.id, cur.id));
    if (live) {
      return [live];
    }
  }
  return [];
}

export function describeTargets(list) {
  return (list || []).map((c) => {
    const name = displayNameForComponent(c);
    return `${chipMeta(c).short}「${name}」`;
  });
}

const CHIP_META = {
  'v-button': { short: '按钮', color: '#409eff' },
  'v-text': { short: '文字', color: '#e6a23c' },
  Picture: { short: '图片', color: '#67c23a' },
  LineComp: { short: '线条', color: '#909399' },
  StreamMedia: { short: '流媒', color: '#f56c6c' },
  Video: { short: '视频', color: '#f56c6c' },
  Music: { short: '音乐', color: '#9b59b6' },
  Weather: { short: '天气', color: '#00bcd4' },
  Carousel: { short: '轮播', color: '#ff9800' },
  Date: { short: '时间', color: '#607d8b' },
  Environment: { short: '环境', color: '#26a69a' },
  Document: { short: '文档', color: '#5c6bc0' },
  Webpage: { short: '网页', color: '#42a5f5' },
  Slider: { short: '滑块', color: '#8d6e63' },
  StatusLight: { short: '状态', color: '#66bb6a' },
  ChildPage: { short: '子页', color: '#7e57c2' },
  Visualization: { short: '可视', color: '#26c6da' },
  Group: { short: '组合', color: '#78909c' },
};

function chipMeta(c) {
  const key = c && c.component;
  return CHIP_META[key] || {
    short: String((c && (c.label || c.component)) || '组件').slice(0, 2),
    color: '#409eff',
  };
}

function stripGeneratedId(text) {
  if (text == null) {
    return '';
  }
  return String(text).replace(/\d{10,}$/g, '').trim();
}

export function displayNameForComponent(c) {
  if (!c) {
    return '组件';
  }
  const alias = stripGeneratedId(c.style && c.style.alias);
  const text =
    typeof c.propValue === 'string' && !/^\d{10,}$/.test(c.propValue.trim())
      ? stripGeneratedId(c.propValue)
      : '';
  return alias || text || c.label || chipMeta(c).short || c.component || '组件';
}

export function buildRefChip(c) {
  if (!c || c.id == null || idsEqual(c.id, 'Back')) {
    return null;
  }
  const meta = chipMeta(c);
  const display = displayNameForComponent(c);
  return {
    id: c.id,
    name: display,
    short: meta.short,
    color: meta.color,
    component: c.component,
    tooltip: `${meta.short} · ${display}`,
  };
}

const BATCH_TOOL_RE = /align_|distribute_|grid_place|delete_batch|bind_mutually|area\.|clipboard/;
const TARGET_TOOL_RE = /^(style\.|attr\.|network\.|event\.|link\.|lock\.|layer\.|button\.|component\.|layout\.)/;

export function bindPlanToLiveTargets(plan, store, extraIds) {
  if (!plan || !Array.isArray(plan.steps)) {
    return plan;
  }
  const selected = collectLiveTargets(store, extraIds);
  let selectedIds = selected.map((c) => c.id);
  if (!selectedIds.length && extraIds && extraIds.length) {
    selectedIds = extraIds.slice();
  }
  if (!selectedIds.length) {
    return plan;
  }
  const next = [];
  plan.steps.forEach((step) => {
    const tool = step.tool || step.name;
    const args = Object.assign({}, step.args || step.parameters || {});
    if (BATCH_TOOL_RE.test(String(tool))) {
      args.refs = selectedIds.slice();
      if (args.buttonRefs) {
        args.buttonRefs = selectedIds.slice();
      }
      next.push({ tool, args });
      return;
    }
    if (TARGET_TOOL_RE.test(String(tool)) || args.ref != null || args.targetRef != null) {
      if (selectedIds.length > 1 && TARGET_TOOL_RE.test(String(tool))) {
        selectedIds.forEach((id) => {
          next.push({ tool, args: Object.assign({}, args, { ref: id }) });
        });
        return;
      }
      if (args.ref != null || TARGET_TOOL_RE.test(String(tool))) {
        const matched = selected.find((c) => idsEqual(c.id, args.ref));
        args.ref = matched ? matched.id : selectedIds[0];
      }
      if (args.targetRef != null) {
        const matchedTarget = selected.find((c) => idsEqual(c.id, args.targetRef));
        if (matchedTarget) {
          args.targetRef = matchedTarget.id;
        }
      }
    }
    next.push({ tool, args });
  });
  plan.steps = next;
  return plan;
}

export function findComponentIndex(store, ref) {
  const list = store.state.componentData || [];
  const comp = findComponentByRef(store, ref);
  if (!comp) return -1;
  return list.findIndex((c) => idsEqual(c.id, comp.id));
}
